import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import RichTextEditor from '@/components/marketplace/RichTextEditor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Megaphone, Send, Plus, Users, BellRing, Copy, Trash2, Square, Loader2, ArrowLeft, MailCheck,
} from 'lucide-react';

/**
 * Announcements — the only non-event-triggered email on the platform.
 *
 * Deliberately separate from the template editor: the 14 triggered templates
 * carry {{variables}} and hand-tested inline styles that a rich text editor
 * would quietly damage, so they keep their raw HTML editor. Here the admin
 * writes prose, picks an audience, sees the real recipient count, test-sends,
 * then confirms a throttled background send.
 */
const STATUS_STYLE = {
  draft: 'bg-muted text-muted-foreground',
  sending: 'bg-blue-500/10 text-blue-700',
  sent: 'bg-[hsl(var(--verified))]/10 text-[hsl(var(--verified))]',
  stopped: 'bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))]',
};

const EMPTY = {
  title: '', subject: '', body_html: '', cta_label: '', cta_url: '',
  audience: 'everyone', post_to_bell: true, show_banner: true,
  bell_text: '', banner_text: '',
};

export const AnnouncementsTab = ({ isAdmin }) => {
  const [rows, setRows] = useState([]);
  const [audiences, setAudiences] = useState({});
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(null); // draft object being edited
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [progressFor, setProgressFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, aud] = await Promise.all([
        api.get('/admin/announcements'),
        api.get('/admin/announcements/audiences'),
      ]);
      setRows(list.data || []);
      setAudiences(aud.data || {});
    } catch (e) {
      toast.error('Could not load announcements', { description: e?.response?.data?.detail });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll while anything is mid-flight so the progress strip climbs live.
  useEffect(() => {
    const anySending = rows.some((r) => r.status === 'sending');
    if (!anySending) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [rows, load]);

  const recipientCount = (audience) => audiences?.[audience]?.count ?? 0;

  const saveDraft = async () => {
    if (!composing.title.trim() || !composing.subject.trim()) return toast.error('Title and subject are required');
    if (!(composing.body_html || '').replace(/<[^>]*>/g, '').trim()) return toast.error('Write the announcement body first');
    setSaving(true);
    try {
      const payload = { ...composing };
      const { data } = composing.id
        ? await api.patch(`/admin/announcements/${composing.id}`, payload)
        : await api.post('/admin/announcements', payload);
      setComposing(data);
      toast.success('Draft saved');
      load();
      return data;
    } catch (e) {
      toast.error('Save failed', { description: e?.response?.data?.detail });
      return null;
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) return toast.error('Enter an address for the test');
    const saved = composing.id ? composing : await saveDraft();
    if (!saved) return;
    try {
      await api.post(`/admin/announcements/${saved.id}/test-send`, { email: testEmail.trim() });
      toast.success(`Test sent to ${testEmail.trim()}`);
    } catch (e) {
      toast.error('Test send failed', { description: e?.response?.data?.detail });
    }
  };

  const doSend = async () => {
    const saved = composing.id ? composing : await saveDraft();
    if (!saved) return;
    try {
      const { data } = await api.post(`/admin/announcements/${saved.id}/send`);
      toast.success(`Sending to ${data.recipient_count} recipients — delivery continues in the background`);
      setConfirmOpen(false);
      setComposing(null);
      setProgressFor(saved.id);
      load();
    } catch (e) {
      toast.error('Send failed', { description: e?.response?.data?.detail });
    }
  };

  const stopSend = async (id) => {
    try {
      await api.post(`/admin/announcements/${id}/stop`);
      toast.success('Stop requested — the current batch will finish, then delivery halts');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not stop'); }
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/admin/announcements/${id}/duplicate`);
      toast.success('Duplicated as a new draft');
      setComposing(data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not duplicate'); }
  };

  const removeDraft = async (id) => {
    try {
      await api.delete(`/admin/announcements/${id}`);
      toast.success('Draft deleted');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete'); }
  };

  if (!isAdmin && !composing) {
    // Moderators read history only — they can still approve/discard queued mail
    // from the Queue tab, but cannot originate a broadcast.
    return (
      <HistoryList
        rows={rows}
        loading={loading}
        readOnly
        onStop={stopSend}
        onDuplicate={duplicate}
        onDelete={removeDraft}
        onOpen={() => {}}
      />
    );
  }

  if (composing) {
    const count = recipientCount(composing.audience);
    return (
      <div className='space-y-4' data-testid='announcement-composer'>
        <div className='flex items-center justify-between gap-3'>
          <Button variant='ghost' size='sm' onClick={() => setComposing(null)} data-testid='announcement-composer-back'>
            <ArrowLeft className='mr-1.5 h-4 w-4' /> Back to announcements
          </Button>
          <Badge className={STATUS_STYLE[composing.status || 'draft']} data-testid='announcement-composer-status'>
            {composing.status || 'draft'}
          </Badge>
        </div>

        <Card className='space-y-4 p-4'>
          <div className='grid gap-3 sm:grid-cols-2'>
            <div>
              <Label>Internal title</Label>
              <Input
                value={composing.title}
                onChange={(e) => setComposing({ ...composing, title: e.target.value })}
                placeholder='Compare launch — Oct 2026'
                data-testid='announcement-title'
              />
            </div>
            <div>
              <Label>Subject line</Label>
              <Input
                value={composing.subject}
                onChange={(e) => setComposing({ ...composing, subject: e.target.value })}
                placeholder='Compare up to 4 listings side by side'
                data-testid='announcement-subject'
              />
            </div>
          </div>

          <div>
            <Label>Body</Label>
            <p className='mb-1.5 text-xs text-muted-foreground'>
              Written in the same editor as blogs. It is wrapped in the standard IndusFact email shell on send.
              You can use <code>{'{{first_name}}'}</code> and <code>{'{{site_url}}'}</code>, or just write plain prose.
            </p>
            <RichTextEditor
              value={composing.body_html || ''}
              onChange={(html) => setComposing({ ...composing, body_html: html })}
            />
          </div>

          <div className='grid gap-3 sm:grid-cols-2'>
            <div>
              <Label>Button label (optional)</Label>
              <Input
                value={composing.cta_label || ''}
                onChange={(e) => setComposing({ ...composing, cta_label: e.target.value })}
                placeholder='Compare listings now'
                data-testid='announcement-cta-label'
              />
            </div>
            <div>
              <Label>Button link</Label>
              <Input
                value={composing.cta_url || ''}
                onChange={(e) => setComposing({ ...composing, cta_url: e.target.value })}
                placeholder='/compare'
                data-testid='announcement-cta-url'
              />
            </div>
          </div>

          <div className='grid gap-3 sm:grid-cols-2'>
            <div>
              <Label>Audience</Label>
              <Select
                value={composing.audience}
                onValueChange={(v) => setComposing({ ...composing, audience: v })}
              >
                <SelectTrigger data-testid='announcement-audience'><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.values(audiences).map((a) => (
                    <SelectItem key={a.key} value={a.key} data-testid={`announcement-audience-${a.key}`}>
                      {a.label} — {a.count}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='flex items-end'>
              <div className='rounded-lg border bg-secondary/40 px-4 py-2' data-testid='announcement-recipient-count'>
                <div className='text-[11px] uppercase tracking-wider text-muted-foreground'>Recipients</div>
                <div className='text-2xl font-bold tabular-nums leading-tight'>{count}</div>
                <div className='text-[11px] text-muted-foreground'>unsubscribed + blocked already excluded</div>
              </div>
            </div>
          </div>

          <div className='space-y-2 rounded-lg border p-3'>
            <label className='flex items-center justify-between gap-3 text-sm'>
              <span className='flex items-center gap-2'><BellRing className='h-4 w-4 text-primary' /> Also post to the notification bell</span>
              <Switch
                checked={!!composing.post_to_bell}
                onCheckedChange={(v) => setComposing({ ...composing, post_to_bell: v })}
                data-testid='announcement-post-to-bell'
              />
            </label>
            <label className='flex items-center justify-between gap-3 text-sm'>
              <span className='flex items-center gap-2'><Megaphone className='h-4 w-4 text-primary' /> Show a dismissible dashboard banner</span>
              <Switch
                checked={!!composing.show_banner}
                onCheckedChange={(v) => setComposing({ ...composing, show_banner: v })}
                data-testid='announcement-show-banner'
              />
            </label>
            {composing.show_banner && (
              <div>
                <Label className='text-xs'>Banner line (optional — falls back to the subject)</Label>
                <Input
                  value={composing.banner_text || ''}
                  onChange={(e) => setComposing({ ...composing, banner_text: e.target.value })}
                  placeholder='New: compare up to 4 listings side by side'
                  data-testid='announcement-banner-text'
                />
              </div>
            )}
          </div>
        </Card>

        <Card className='flex flex-wrap items-end justify-between gap-3 p-4'>
          <div className='flex flex-wrap items-end gap-2'>
            <div>
              <Label className='text-xs'>Test send to</Label>
              <Input
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder='you@indusfact.com'
                className='w-56'
                data-testid='announcement-test-email'
              />
            </div>
            <Button variant='outline' onClick={sendTest} data-testid='announcement-test-send'>
              <MailCheck className='mr-1.5 h-4 w-4' /> Send test
            </Button>
          </div>
          <div className='flex items-center gap-2'>
            <Button variant='outline' onClick={saveDraft} disabled={saving} data-testid='announcement-save-draft'>
              {saving ? <Loader2 className='mr-1.5 h-4 w-4 animate-spin' /> : null} Save draft
            </Button>
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={composing.status && composing.status !== 'draft'}
              data-testid='announcement-send-open'
            >
              <Send className='mr-1.5 h-4 w-4' /> Send to {count}
            </Button>
          </div>
        </Card>

        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent data-testid='announcement-confirm-dialog'>
            <DialogHeader>
              <DialogTitle>Send this announcement?</DialogTitle>
              <DialogDescription>
                It will go to <strong>{count}</strong> recipients in the audience
                &ldquo;{audiences?.[composing.audience]?.label || composing.audience}&rdquo;.
                Delivery runs in the background in small batches and can be stopped mid-flight.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant='outline' onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button onClick={doSend} data-testid='announcement-send-confirm'>
                <Send className='mr-1.5 h-4 w-4' /> Yes, send to {count}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className='space-y-4' data-testid='announcements-tab'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='flex flex-wrap gap-2'>
          {Object.values(audiences).map((a) => (
            <div key={a.key} className='rounded-lg border bg-card px-3 py-1.5 text-xs' data-testid={`audience-count-${a.key}`}>
              <span className='text-muted-foreground'>{a.label}: </span>
              <span className='font-semibold tabular-nums'>{a.count}</span>
            </div>
          ))}
        </div>
        <Button onClick={() => { setComposing({ ...EMPTY }); setProgressFor(null); }} data-testid='announcement-new'>
          <Plus className='mr-1.5 h-4 w-4' /> New announcement
        </Button>
      </div>

      <HistoryList
        rows={rows}
        loading={loading}
        highlight={progressFor}
        onStop={stopSend}
        onDuplicate={duplicate}
        onDelete={removeDraft}
        onOpen={(row) => setComposing(row)}
      />
    </div>
  );
};

const HistoryList = ({ rows, loading, readOnly, highlight, onStop, onDuplicate, onDelete, onOpen }) => {
  if (loading) return <div className='py-10 text-center text-muted-foreground'>Loading…</div>;
  if (!rows.length) {
    return (
      <Card className='p-8 text-center' data-testid='announcements-empty'>
        <Megaphone className='mx-auto h-8 w-8 text-muted-foreground' />
        <div className='mt-2 font-semibold'>No announcements yet</div>
        <p className='mx-auto mt-1 max-w-md text-sm text-muted-foreground'>
          Announcements are one-off product news — written here, delivered to a chosen audience with a working unsubscribe.
        </p>
      </Card>
    );
  }
  return (
    <div className='space-y-2' data-testid='announcements-history'>
      {rows.map((row) => {
        const total = row.recipient_count || 0;
        const done = (row.sent_count || 0) + (row.failed_count || 0) + (row.queued_count || 0);
        const pct = total ? Math.round((done / total) * 100) : 0;
        return (
          <Card
            key={row.id}
            className={`p-4 ${highlight === row.id ? 'ring-1 ring-primary' : ''}`}
            data-testid={`announcement-row-${row.id}`}
          >
            <div className='flex flex-wrap items-start justify-between gap-3'>
              <div className='min-w-0'>
                <div className='flex items-center gap-2'>
                  <span className='truncate font-semibold'>{row.title}</span>
                  <Badge className={STATUS_STYLE[row.status]} data-testid={`announcement-status-${row.id}`}>{row.status}</Badge>
                </div>
                <div className='truncate text-sm text-muted-foreground'>{row.subject}</div>
                <div className='mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground'>
                  <span className='inline-flex items-center gap-1'><Users className='h-3 w-3' /> {row.audience}</span>
                  <span data-testid={`announcement-counts-${row.id}`}>
                    {total} recipients · {row.sent_count || 0} sent · {row.failed_count || 0} failed
                    {row.queued_count ? ` · ${row.queued_count} held for review` : ''}
                  </span>
                  {row.bell_sent_count ? <span className='inline-flex items-center gap-1'><BellRing className='h-3 w-3' /> {row.bell_sent_count} bell</span> : null}
                  <span>{row.created_at ? new Date(row.created_at).toLocaleDateString() : ''}</span>
                </div>
                {row.status === 'sending' && (
                  <div className='mt-2 h-1.5 w-56 overflow-hidden rounded-full bg-secondary' data-testid={`announcement-progress-${row.id}`}>
                    <div className='h-full bg-primary transition-all' style={{ width: `${pct}%` }} />
                  </div>
                )}
              </div>
              {!readOnly && (
                <div className='flex flex-wrap items-center gap-2'>
                  {row.status === 'draft' && (
                    <>
                      <Button size='sm' onClick={() => onOpen(row)} data-testid={`announcement-edit-${row.id}`}>Open draft</Button>
                      <Button size='sm' variant='ghost' className='text-destructive' onClick={() => onDelete(row.id)} data-testid={`announcement-delete-${row.id}`}>
                        <Trash2 className='h-4 w-4' />
                      </Button>
                    </>
                  )}
                  {row.status === 'sending' && (
                    <Button size='sm' variant='outline' onClick={() => onStop(row.id)} data-testid={`announcement-stop-${row.id}`}>
                      <Square className='mr-1.5 h-3.5 w-3.5' /> Stop
                    </Button>
                  )}
                  {row.status !== 'draft' && (
                    <Button size='sm' variant='outline' onClick={() => onDuplicate(row.id)} data-testid={`announcement-duplicate-${row.id}`}>
                      <Copy className='mr-1.5 h-3.5 w-3.5' /> Duplicate
                    </Button>
                  )}
                </div>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export default AnnouncementsTab;
